import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { useLoanHistory } from '@/hooks/useLoanHistory';
import type { LoanHistoryEntry } from '@/hooks/useLoanHistory';

interface LoanHistoryDialogProps {
  itemId: string;
  itemName: string;
  isOwner?: boolean;
}

const BADGE_STYLES = {
  active: {
    background: T.warningSoft,
    color: T.warning,
    border: `1px solid ${T.warningBorder}`,
  },
  returned: {
    background: T.successSoft,
    color: T.success,
    border: `1px solid ${T.successBorder}`,
  },
  overdue: {
    background: T.dangerSoft,
    color: T.danger,
    border: `1px solid ${T.dangerBorder}`,
  },
};

const BADGE_BASE = {
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 10px',
  borderRadius: RADIUS.dot,
  letterSpacing: '0.03em',
  display: 'inline-block',
  whiteSpace: 'nowrap',
} as const;

const CONDITION_BADGE_STYLES: Record<string, { background: string; color: string; border: string }> = {
  good: { background: T.successSoft, color: T.success, border: `1px solid ${T.successBorder}` },
  minor_wear: { background: T.warningSoft, color: T.warning, border: `1px solid ${T.warningBorder}` },
  damaged: { background: T.dangerSoft, color: T.danger, border: `1px solid ${T.dangerBorder}` },
};

const CONDITION_LABELS: Record<string, string> = {
  good: 'Good',
  minor_wear: 'Minor wear',
  damaged: 'Damaged',
};

/**
 *
 * @param root0
 * @param root0.entry
 */
function StatusBadge({ entry }: { entry: LoanHistoryEntry }) {
  if (entry.overdue_days != null && entry.overdue_days > 0) {
    return <span style={{ ...BADGE_BASE, ...BADGE_STYLES.overdue }}>Overdue</span>;
  }
  if (entry.returned_at) {
    return <span style={{ ...BADGE_BASE, ...BADGE_STYLES.returned }}>Returned</span>;
  }
  return <span style={{ ...BADGE_BASE, ...BADGE_STYLES.active }}>Active</span>;
}

/**
 *
 * @param root0
 * @param root0.itemId
 * @param root0.itemName
 */
export function LoanHistoryDialog({ itemId, itemName, isOwner }: LoanHistoryDialogProps) {
  const [open, setOpen] = useState(false);
  const { data: history, isLoading } = useLoanHistory(open ? itemId : null);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          width: '100%',
          padding: '14px 0',
          borderRadius: RADIUS.md,
          background: T.surface,
          border: `1px solid ${T.border}`,
          color: T.textSec,
          fontSize: 15,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          letterSpacing: '-0.01em',
        }}
      >
        🕘 Loan History
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="w-[90vw] max-w-[90vw] sm:max-w-2xl p-0 overflow-hidden [&>button]:bg-transparent [&>button]:border-0 [&>button]:shadow-none [&>button]:text-[var(--close-color)] [&>button:hover]:text-[var(--close-hover)] [&>button]:opacity-100 [&>button]:data-[state=open]:bg-transparent [&>button]:ring-0 [&>button]:ring-offset-0"
          style={
            {
              '--close-color': T.textMuted,
              '--close-hover': T.textSec,
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.xl,
              boxShadow: SHADOW.lg,
            } as React.CSSProperties
          }
        >
          <DialogHeader
            className="px-5 pt-5 pb-4"
            style={{ borderBottom: `1px solid ${T.border}` }}
          >
            <DialogTitle
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: T.text,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              <span style={{ color: T.accent }}>{itemName}</span> Loan History
            </DialogTitle>
          </DialogHeader>

          <div
            className="overflow-y-auto"
            style={{ maxHeight: 'min(60vh, 480px)', padding: '0 20px' }}
          >
            {isLoading ? (
              <p
                style={{ fontSize: 14, color: T.textMuted, textAlign: 'center', padding: '24px 0' }}
              >
                Loading…
              </p>
            ) : !history || history.length === 0 ? (
              <p
                style={{ fontSize: 14, color: T.textMuted, textAlign: 'center', padding: '24px 0' }}
              >
                No loan history for this item.
              </p>
            ) : (
              <div>
                {history.map((entry, index) => (
                  <div
                    key={entry.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '12px 0',
                      borderBottom: index < history.length - 1 ? `1px solid ${T.border}` : 'none',
                    }}
                  >
                    {/* Left: loanee name + loan date */}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: T.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.borrower_name || 'Unknown'}
                      </div>
                      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                        {new Date(entry.borrowed_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                        {entry.returned_at && (
                          <>
                            {' '}
                            →{' '}
                            {new Date(entry.returned_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </>
                        )}
                        {!entry.returned_at && <span style={{ color: T.borderStrong }}> → —</span>}
                      </div>
                      {entry.notes && (
                        <div style={{ marginTop: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 400, color: T.textMuted }}>Note: </span>
                          <span style={{ fontSize: 14, fontWeight: 400, color: T.textSec, lineHeight: 1.65 }}>
                            {entry.notes}
                          </span>
                        </div>
                      )}
                      {isOwner && entry.return_notes && (
                        <div style={{ marginTop: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 400, color: T.textMuted }}>Return note: </span>
                          <span style={{ fontSize: 14, fontWeight: 400, color: T.textSec, lineHeight: 1.65 }}>
                            {entry.return_notes}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Right: duration + status badge */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 4,
                        flexShrink: 0,
                      }}
                    >
                      <div style={{ fontSize: 12, color: T.textMuted }}>
                        {entry.duration_days} day{entry.duration_days !== 1 ? 's' : ''}
                      </div>
                      <StatusBadge entry={entry} />
                      {isOwner && entry.return_condition && CONDITION_BADGE_STYLES[entry.return_condition] && (
                        <span
                          title={entry.return_notes || undefined}
                          style={{
                            ...BADGE_BASE,
                            ...CONDITION_BADGE_STYLES[entry.return_condition],
                          }}
                        >
                          {CONDITION_LABELS[entry.return_condition] ?? entry.return_condition}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom padding */}
          <div style={{ height: 20 }} />
        </DialogContent>
      </Dialog>
    </>
  );
}
